import { useState, useMemo, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Modal,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import LockIconWrapper from '../../components/LockIconWrapper';
import DateSelect from './components/DateSelect';
import TimeSelect from './components/TimeSelect';
import PersonSelect from './components/PersonSelect';
import PackageAndUpsellSelect from './components/PackageAndUpsellSelect';
import CustomerData from './components/CustomerData';
import Overview from './components/Overview';
import EntertainmentSelect from './components/EntertainmentSelect';
import CopyCustomerDataSelect from './components/CopyCustomerDataSelect';
import { useBooking } from '../../../context/bookingContext';
import { useAuth } from '../../../context/authContext';
import SocketIOContext from '../../../context/socketIOContext';
import dayjs from 'dayjs';
import DurationSelect from './components/DurationSelect';
import useTemporaryResevation from '../../../hooks/useTemporaryResevation';
import useInternalBooking from '../../../hooks/useInternalBooking';
import useWalkInSubmissions from '../../../hooks/useWalkInSubmissions';
import MailDeliveryAnimation from '../../emails/MailDeliveryAnimation';

export default function CreateBookingModal({
  open,
  onClose,
  onSuccess,
  onAfterCreate,
  branchId = null,
  entertainmentId = null,
}) {
  const {
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedDuration,
    setSelectedDuration,
    selectedCategories,
    setSelectedCategories,
    selectedPackage,
    setSelectedPackage,
    selectedUpsells,
    setSelectedUpsells,
    selectedBranch,
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    selectedPricingId,
    entertainmentError,
    setEntertainmentError,
    tempReservationId,
    setTempReservationId,
    customerData,
    calculateTotalAmount,
    resetBooking,
    priceOverrides,
    overbooking,
    setTimeTags,
    getCurrentTags,
    fetchPackages,
    fetchUpsells,
    calculateLineItems,
    specialLineItems,
    areSpecialLineItemsComplete,
    isUpsellsLoading,
    isPackagesLoading,
    resources,
    packages,
    upsells,
  } = useBooking();

  const {
    addTemporaryReservation,
    updatePackageOfTemporaryReservation,
    updateUpsellsOfTemporaryReservation,
    invalidateTemporaryReservation,
  } = useTemporaryResevation();
  const {
    convertTemporaryReservationToBooking,
    getCustomerConfirmationMailStatus,
    resendBookingConfirmation,
    updateInternalBooking,
  } = useInternalBooking();
  const { deleteWalkInSubmission } = useWalkInSubmissions();
  const { checkRightAny, preferences } = useAuth();
  const showMailDeliveryStep = preferences?.showMailDeliveryStep !== false;
  const { isConnected, emit: socketEmit, on: socketOn } = useContext(SocketIOContext) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showRequestOnlyConfirm, setShowRequestOnlyConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('booking');
  const [isDayBookable, setIsDayBookable] = useState(true);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [mailDeliveryStatus, setMailDeliveryStatus] = useState(null);
  const [mailStatusPollCount, setMailStatusPollCount] = useState(0);
  const [isMailStatusLoading, setIsMailStatusLoading] = useState(false);
  const [isResendingMail, setIsResendingMail] = useState(false);
  const [correctedMail, setCorrectedMail] = useState('');
  const [mailCorrectionError, setMailCorrectionError] = useState('');
  const [reservationCartSummary, setReservationCartSummary] = useState(null);
  const overviewRef = useRef(null);
  const customerDataRef = useRef(null);
  // Id of a pre-submitted walk-in entry the staff member took over, if any.
  // Deleted once the booking is completed (the backend TTL is the backstop).
  const selectedWalkInSubmissionIdRef = useRef(null);
  const reservationSnapshotRef = useRef(null);
  const createdBookingFollowUpRef = useRef({ shouldOpen: false, bookingId: null });
  const getCustomerConfirmationMailStatusRef = useRef(getCustomerConfirmationMailStatus);

  const buildReservationSnapshot = () => {
    const categories = (selectedCategories || [])
      .map(category => ({
        name: category.name,
        count: parseInt(String(category.quantity), 10) || 0,
      }))
      .filter(c => c.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      productId: selectedProduct,
      entertainmentId: selectedEntertainment,
      resourceId: selectedResource || null,
      dateOfEvent: selectedDate?.format?.('YYYY-MM-DD') ?? null,
      categories,
      pricingId: selectedPricingId,
      startTime: selectedTime,
      duration: selectedDuration,
      overbooking: overbooking,
    };
  };

  const isSnapshotEqual = (a, b) => {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const invalidateReservationIfAny = async () => {
    if (!tempReservationId) return;
    try {
      await invalidateTemporaryReservation(tempReservationId);
    } catch (error) {
      toast.error('Fehler beim Stornieren der Reservierung');
      console.error('Error invalidating temporary reservation:', error);
    } finally {
      setTempReservationId(null);
      setReservationCartSummary(null);
      reservationSnapshotRef.current = null;
    }
  };

  const getCreateReservationErrorToast = error => {
    const raw = String(error?.message || '').trim();
    if (!raw) return 'Fehler beim Erstellen der Reservierung';
    if (raw.includes('Failed to fetch')) return 'Backend nicht erreichbar';
    if (raw.startsWith('401')) return 'Sitzung abgelaufen – bitte erneut anmelden';
    if (raw.startsWith('403')) return 'Keine Berechtigung zum Erstellen der Reservierung';
    if (raw.startsWith('500')) return 'Fehler beim Erstellen der Reservierung (Backend)';
    return `Fehler beim Erstellen der Reservierung (${raw})`;
  };

  const isRequestOnly = useMemo(() => {
    const tags = getCurrentTags(false, true) || [];
    return Array.isArray(tags) && tags.includes('requiresEmployeeConfirmation');
  }, [getCurrentTags]);

  const hasConfirmPendingRight = useMemo(
    () => checkRightAny('internalBooking:special:confirmpending'),
    [checkRightAny]
  );

  // Key used to persist last customer data for quick reuse
  const LAST_CUSTOMER_DATA_KEY = 'fz:lastCustomerData';

  // Persist current customer data together with a timestamp
  const persistLastCustomerData = (data = customerData) => {
    try {
      const payload = { timestamp: Date.now(), data };
      localStorage.setItem(LAST_CUSTOMER_DATA_KEY, JSON.stringify(payload));
    } catch (err) {
      // Only log; UX should not be blocked if storage fails
      console.error('Failed to persist last customer data', err);
    }
  };
  // Reset time and duration when product changes
  useEffect(() => {
    setSelectedTime(null);
    setSelectedDuration(null);
  }, [selectedProduct]);

  useEffect(() => {
    getCustomerConfirmationMailStatusRef.current = getCustomerConfirmationMailStatus;
  }, [getCustomerConfirmationMailStatus]);

  useEffect(() => {
    if (currentStep !== 'mailDelivery' || !createdBookingId) return;
    const status = mailDeliveryStatus?.status;
    if (['delivered', 'failed', 'bounced', 'skipped'].includes(status)) return;

    let cancelled = false;
    const pollDelayMs = mailStatusPollCount === 0 ? 250 : Math.min(6500, 650 + mailStatusPollCount * 520);

    const timeout = window.setTimeout(
      async () => {
        setIsMailStatusLoading(true);
        try {
          const response = await getCustomerConfirmationMailStatusRef.current(createdBookingId);
          if (!cancelled) {
            setMailDeliveryStatus(response);
            setMailStatusPollCount(count => count + 1);
          }
        } catch (error) {
          if (!cancelled) {
            setMailDeliveryStatus({
              status: 'failed',
              reason: 'status_lookup_failed',
              mail: { errorMessage: 'Mailstatus konnte nicht abgerufen werden.' },
            });
          }
        } finally {
          if (!cancelled) {
            setIsMailStatusLoading(false);
          }
        }
      },
      pollDelayMs
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [createdBookingId, currentStep, mailDeliveryStatus?.status, mailStatusPollCount]);

  useEffect(() => {
    if (
      currentStep !== 'mailDelivery' ||
      !createdBookingId ||
      !socketEmit ||
      typeof socketEmit !== 'function'
    ) {
      return undefined;
    }

    socketEmit('join_internal_booking_mail_status', {
      bookingId: createdBookingId,
    });
    return undefined;
  }, [createdBookingId, currentStep, socketEmit, isConnected]);

  useEffect(() => {
    if (
      currentStep !== 'mailDelivery' ||
      !createdBookingId ||
      !socketOn ||
      typeof socketOn !== 'function'
    ) {
      return undefined;
    }

    const unsubscribe = socketOn(
      'internalBooking:customerConfirmationMailStatus',
      payload => {
        if (!payload || String(payload.bookingId) !== String(createdBookingId)) return;
        setMailDeliveryStatus(current => ({
          ...(current || {}),
          ...payload,
        }));
      }
    );

    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [createdBookingId, currentStep, socketOn]);

  const handleDateSelect = (date, dayData) => {
    if (!date) {
      setSelectedDate(null);
      setIsDayBookable(false);
      setSelectedTime(null);
      return;
    }
    const dateObj = dayjs(date);
    setSelectedDate(dateObj);
    setIsDayBookable(dayData ? dayData.isBookable : false);
    setSelectedTime(null);
  };

  const handleDurationSelect = duration => {
    setSelectedDuration(duration);
    setSelectedTime(null);
  };

  const handleTimeSelect = time => {
    setSelectedTime(time);
  };

  const handlePersonSelect = categories => {
    setSelectedCategories(categories);
  };

  const handleClose = async (invalidate = true) => {
    if (invalidate && tempReservationId) {
      try {
        await invalidateTemporaryReservation(tempReservationId);
      } catch (error) {
        toast.error('Fehler beim Stornieren der Reservierung');
        console.error('Error invalidating temporary reservation:', error);
      }
    }
    reservationSnapshotRef.current = null;
    setCreatedBookingId(null);
    setMailDeliveryStatus(null);
    setMailStatusPollCount(0);
    setCorrectedMail('');
    setMailCorrectionError('');
    setCurrentStep('booking');
    createdBookingFollowUpRef.current = { shouldOpen: false, bookingId: null };
    resetBooking();
    onClose();
  };

  const finishCreatedBookingFlow = () => {
    const followUp = createdBookingFollowUpRef.current;
    handleClose(false);
    if (followUp.shouldOpen && onAfterCreate && followUp.bookingId) {
      onAfterCreate({ bookingId: followUp.bookingId });
    }
  };

  const handleCloseRequest = () => {
    if (currentStep === 'mailDelivery') {
      finishCreatedBookingFlow();
      return;
    }
    setShowConfirmClose(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    handleClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const validateMailInput = value => {
    const normalized = String(value || '').trim();
    if (!normalized) return 'Bitte gib eine E-Mail-Adresse ein';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      return 'Bitte gib eine gültige E-Mail-Adresse ein';
    }
    return '';
  };

  const refreshMailDeliveryStatus = async () => {
    if (!createdBookingId) return null;
    setIsMailStatusLoading(true);
    try {
      const status = await getCustomerConfirmationMailStatus(createdBookingId);
      setMailDeliveryStatus(status);
      return status;
    } finally {
      setIsMailStatusLoading(false);
    }
  };

  const handleRetryMailDelivery = async () => {
    if (!createdBookingId || isResendingMail) return;
    const normalizedMail = correctedMail.trim();
    const validationError = validateMailInput(normalizedMail);
    if (validationError) {
      setMailCorrectionError(validationError);
      return;
    }

    setMailCorrectionError('');
    setIsResendingMail(true);
    try {
      const updateResponse = await updateInternalBooking(createdBookingId, { mail: normalizedMail });
      if (!updateResponse) return;
      await resendBookingConfirmation(createdBookingId);
      setMailDeliveryStatus({
        status: 'pending',
        reason: 'resent',
        recipient: normalizedMail,
        mail: null,
      });
      setMailStatusPollCount(0);
      toast.success('Bestätigungsmail wird erneut versendet');
    } catch (error) {
      toast.error('Fehler beim erneuten Versenden der Bestätigungsmail');
      console.error('Error resending booking confirmation:', error);
    } finally {
      setIsResendingMail(false);
    }
  };

  const handleGoToPackages = async () => {
    // Always tell the user which selections are missing instead of leaving the
    // button silently disabled.
    const missing = [];
    if (!selectedDate) missing.push('Datum');
    if (!selectedDuration) missing.push('Dauer');
    if (!selectedTime) missing.push('Uhrzeit');
    if (!selectedCategories || selectedCategories.length === 0) missing.push('Personenanzahl');
    if (missing.length > 0) {
      toast.error(`Bitte fülle aus: ${missing.join(', ')}`);
      return;
    }

    const currentSnapshot = buildReservationSnapshot();
    const shouldRecreate =
      !tempReservationId ||
      !reservationSnapshotRef.current ||
      !isSnapshotEqual(reservationSnapshotRef.current, currentSnapshot);

    if (shouldRecreate) {
      setIsLoading(true);
      setCurrentStep('packages');
      try {
        if (tempReservationId) {
          await invalidateReservationIfAny();
        }

        const reservationData = buildReservationSnapshot();
        setReservationCartSummary(null);

        const tempReservation = await addTemporaryReservation(reservationData);

        setTimeTags(tempReservation.tags);
        const newTags = getCurrentTags(true, false).concat(tempReservation.tags);
        const [fetchedPackages, fetchedUpsells] = await Promise.all([
          fetchPackages(newTags),
          fetchUpsells(newTags),
        ]);

        if (!tempReservation || !tempReservation.id) {
          throw new Error('Failed to create temporary reservation');
        }
        setTempReservationId(tempReservation.id);
        reservationSnapshotRef.current = reservationData;

        // Skip packages step if no packages or upsells are available
        if (fetchedPackages.length === 0 && fetchedUpsells.length === 0) {
          setCurrentStep('customer');
        } else {
          setCurrentStep('packages');
        }
      } catch (error) {
        toast.error(getCreateReservationErrorToast(error));
        setCurrentStep('booking');
        return;
      } finally {
        setIsLoading(false);
      }
    } else {
      // For existing reservations, check if we should skip packages
      if (packages.length === 0 && upsells.length === 0) {
        setCurrentStep('customer');
      } else {
        setCurrentStep('packages');
      }
    }
  };

  const handleGoToCustomer = async () => {
    if (!tempReservationId) {
      return;
    }
    const sanitizedUpsells = [];
    const participantCount = selectedCategories.reduce((sum, category) => {
      const raw = category?.quantity ?? category?.count ?? 0;
      return sum + (parseInt(String(raw), 10) || 0);
    }, 0);
    const selectedPackageMinimum = Number(selectedPackage?.minimumCount);
    if (
      selectedPackage &&
      Number.isFinite(selectedPackageMinimum) &&
      selectedPackageMinimum >= 0 &&
      participantCount < Math.floor(selectedPackageMinimum)
    ) {
      toast.error(
        `Paket nicht waehlbar: mindestens ${Math.floor(selectedPackageMinimum)} Teilnehmer erforderlich.`
      );
      return;
    }
    if (selectedUpsells && selectedUpsells.length > 0) {
      for (const upsell of selectedUpsells) {
        const rawQuantity = parseInt(upsell.quantity, 10);
        const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : 0;
        const max =
          Number.isFinite(upsell.maximumCount) && upsell.maximumCount >= 0
            ? Math.floor(upsell.maximumCount)
            : null;

        if (max !== null && quantity > max) {
          toast.error(`Maximale Anzahl für ${upsell.name} ist ${max}.`);
          return;
        }
        const minimum = Number(upsell.minimumCount);
        if (Number.isFinite(minimum) && minimum >= 0 && participantCount < Math.floor(minimum)) {
          toast.error(
            `${upsell.name} ist nicht waehlbar: mindestens ${Math.floor(minimum)} Teilnehmer erforderlich.`
          );
          return;
        }

        if (quantity > 0) {
          sanitizedUpsells.push({ upsellId: upsell.id, count: quantity });
        }
      }
    }

    setIsLoading(true);
    try {
      const packageResponse = await updatePackageOfTemporaryReservation(
        tempReservationId,
        selectedPackage?.id || null
      );
      const upsellResponse = await updateUpsellsOfTemporaryReservation(tempReservationId, sanitizedUpsells);
      setReservationCartSummary(
        upsellResponse?.cartSummary || packageResponse?.cartSummary || null
      );
      setCurrentStep('customer');
    } catch (error) {
      const errorCode =
        error?.code || error?.response?.data?.code || error?.response?.code || error?.data?.code;
      if (errorCode === 'UPGRADE_MINIMUM_COUNT_NOT_REACHED') {
        toast.error(
          'Auswahl nicht möglich: Mindestteilnehmerzahl fuer Paket/Zusatzleistung unterschritten.'
        );
      } else {
        toast.error('Fehler beim Speichern von Paket/Zusatzleistungen');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const performCompleteBooking = async (confirmIfPending = false) => {
    if (!tempReservationId) {
      return;
    }

    setIsLoading(true);

    try {
      const totalAmount = overviewRef.current?.getPayableTotalAmount?.() ?? calculateTotalAmount();
      const paymentType = 'offline';

      const formattedCustomerData = (() => {
        if (customerData?.isWalkIn) {
          // Only send minimal fields for walk-in
          return {
            firstName: customerData.firstName,
            lastName: customerData.lastName,
            mail: customerData.mail,
          };
        }
        if (customerData.birthdate) return customerData;
        const { birthdate, ...dataWithoutBirthdate } = customerData;
        return dataWithoutBirthdate;
      })();

      // filter out incomplete special line items (should never be necessary)
      const completeSpecialLineItems = specialLineItems.filter(
        item => item.description.trim() && item.count > 0 && item.price > 0
      );

      // Get comment, disableSelfService, suppressCustomerEmails and capacity overrides from Overview component
      const comment = overviewRef.current?.getComment() || '';
      const disableSelfService = overviewRef.current?.getDisableSelfService() || false;
      const suppressCustomerEmails = overviewRef.current?.getSuppressCustomerEmails?.() || false;
      const capacityParticipantCount = overviewRef.current?.getCapacityParticipantCount?.();
      const capacityLaneCount = overviewRef.current?.getCapacityLaneCount?.();

      const toastMessage = confirmIfPending
        ? `Die Anfrage, die Buchung für ${customerData.firstName} ${customerData.lastName} direkt zu bestätigen, wurde gespeichert und kann nach Identifizierung abgeschlossen werden`
        : isRequestOnly
          ? `Die Buchungsanfrage für ${customerData.firstName} ${customerData.lastName} wurde gespeichert und kann später bestätigt werden`
          : null;
      const successToastMessage = isRequestOnly
        ? confirmIfPending
          ? 'Buchung erfolgreich erstellt und bestätigt'
          : `Buchungsanfrage für ${customerData.firstName} ${customerData.lastName} gespeichert`
        : `Buchung für ${customerData.firstName} ${customerData.lastName} erfolgreich erstellt`;

      const displayInfo = {
        Typ: confirmIfPending ? 'Buchung erstellen (Direkt bestätigen)' : 'Buchung',
        Kunde: `${customerData.firstName} ${customerData.lastName}`,
        Datum: selectedDate.format('DD.MM.YYYY'),
        Uhrzeit: selectedTime,
        Betrag: `${parseFloat(totalAmount).toFixed(2).replace('.', ',')}€`,
      };

      const onStored = () => {
        // Close the modal when the request is stored due to authentication failure
        handleClose(false);
      };

      const onSuccessCallback = response => {
        // A pre-submitted walk-in entry that was taken over is now consumed —
        // delete it (best-effort; the 20-min backend TTL is the backstop).
        if (selectedWalkInSubmissionIdRef.current) {
          const submissionId = selectedWalkInSubmissionIdRef.current;
          selectedWalkInSubmissionIdRef.current = null;
          deleteWalkInSubmission(submissionId).catch(() => {});
        }
        if (onSuccess) {
          onSuccess(response);
        }
        const shouldOpen = overviewRef.current?.getOpenAfterSave?.() || false;
        const newBookingId =
          response?.internalBooking?._id ||
          response?.internalBooking?.id ||
          response?.bookingId ||
          response?.booking?._id ||
          response?._id ||
          null;

        if (newBookingId && shouldOpen && onAfterCreate) {
          handleClose(false);
          onAfterCreate({ bookingId: newBookingId });
          return;
        }

        if (newBookingId && showMailDeliveryStep) {
          createdBookingFollowUpRef.current = { shouldOpen, bookingId: newBookingId };
          setCreatedBookingId(newBookingId);
          setCorrectedMail(customerData?.mail || '');
          setMailCorrectionError('');
          setMailDeliveryStatus(null);
          setMailStatusPollCount(0);
          setCurrentStep('mailDelivery');
          return;
        }

        handleClose(false);
      };

      const response = await convertTemporaryReservationToBooking(
        tempReservationId,
        formattedCustomerData,
        totalAmount,
        paymentType,
        priceOverrides,
        completeSpecialLineItems,
        toastMessage,
        onStored,
        displayInfo,
        onSuccessCallback,
        successToastMessage,
        comment,
        Boolean(customerData?.isWalkIn),
        confirmIfPending,
        disableSelfService,
        capacityParticipantCount,
        capacityLaneCount,
        suppressCustomerEmails
      );

      if (response) {
        onSuccessCallback(response);
      }
    } catch (error) {
      console.error('Error completing booking:', error);
      // Only show error if it's not an authentication failure
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Abschluss der Buchung');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteBooking = async () => {
    if (isRequestOnly && !hasConfirmPendingRight) {
      await performCompleteBooking(false);
      return;
    }
    if (isRequestOnly && hasConfirmPendingRight) {
      setShowRequestOnlyConfirm(true);
      return;
    }
    await performCompleteBooking(false);
  };

  const handleDateError = error => {
    setEntertainmentError(error);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedDuration(null);
    setSelectedCategories([]);
    setSelectedPackage(null);
    setSelectedUpsells([]);
  };

  const getMailDeliveryCopy = () => {
    const status = mailDeliveryStatus?.status || 'pending';
    if (status === 'delivered') {
      return {
        severity: 'success',
        title: 'Bestätigungsmail wurde zugestellt',
        text: 'Die Bestätigungsmail wurde erfolgreich an den Kunden zugestellt.',
      };
    }
    if (status === 'skipped') {
      return {
        severity: 'info',
        title: 'Es wurde keine Bestätigungsmail versendet',
        text:
          mailDeliveryStatus?.reason === 'suppressed_by_booking'
            ? 'Für diese Buchung wurden Kundenmails deaktiviert.'
            : 'Für diese Buchung ist keine verwendbare E-Mail-Adresse hinterlegt.',
      };
    }
    if (status === 'failed' || status === 'bounced') {
      const errorMessage =
        mailDeliveryStatus?.mail?.errorMessage ||
        mailDeliveryStatus?.bounceState?.lastErrorMessage ||
        'Die Adresse konnte nicht erreicht werden.';
      return {
        severity: 'error',
        title: 'Bestätigungsmail wurde nicht zugestellt',
        text: errorMessage,
      };
    }
    return {
      severity: 'info',
      title: 'Bestätigungsmail wird zugestellt',
      text: 'Die Buchung ist gespeichert. Wir warten auf die Bestätigung der Zustellung.',
    };
  };

  const renderMailAnimation = () => (
    <MailDeliveryAnimation status={mailDeliveryStatus?.status || 'pending'} />
  );

  const renderMailDeliveryContent = () => {
    const copy = getMailDeliveryCopy();
    const status = mailDeliveryStatus?.status || 'pending';
    const canCorrectMail = status === 'failed' || status === 'bounced';

    return (
      <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center', py: { xs: 1, md: 2 } }}>
        {renderMailAnimation()}
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
            {copy.title}
          </Typography>
          <Typography color='text.secondary' sx={{ maxWidth: 680 }}>
            {copy.text}
          </Typography>
        </Box>
        {mailDeliveryStatus?.recipient && (
          <Typography variant='body2' color='text.secondary'>
            Empfänger: <strong>{mailDeliveryStatus.recipient}</strong>
          </Typography>
        )}
        {status === 'pending' && (
          <Alert severity='info' sx={{ width: '100%', maxWidth: 680, textAlign: 'left' }}>
            Die Buchung ist gespeichert. Der Status aktualisiert sich automatisch, sobald die Mail
            zugestellt wurde oder die Zustellung fehlschlägt.
          </Alert>
        )}
        {status === 'pending' && mailStatusPollCount >= 22 && (
          <Alert severity='warning' sx={{ width: '100%', maxWidth: 680, textAlign: 'left' }}>
            Die Rückmeldung dauert ungewöhnlich lange. Du kannst den Schritt überspringen oder den
            Status manuell prüfen – der Abgleich läuft im Hintergrund weiter.
          </Alert>
        )}
        {canCorrectMail && (
          <Box sx={{ width: '100%', maxWidth: 560 }}>
            <TextField
              label='E-Mail-Adresse korrigieren'
              type='email'
              value={correctedMail}
              onChange={event => {
                setCorrectedMail(event.target.value);
                setMailCorrectionError('');
              }}
              onBlur={() => setMailCorrectionError(validateMailInput(correctedMail))}
              error={Boolean(mailCorrectionError)}
              helperText={mailCorrectionError || 'Die Bestätigung wird nach dem Speichern erneut versendet.'}
              fullWidth
            />
          </Box>
        )}
      </Stack>
    );
  };

  // Persistent Step Components
  const renderContent = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: currentStep === 'booking' ? 'block' : 'none' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minHeight: { md: '100%' },
          }}
        >
          <EntertainmentSelect branchId={branchId} entertainmentId={entertainmentId} />
          {entertainmentError ? (
            <Alert severity='error' sx={{ mb: 2 }}>
              {entertainmentError.toString()}
            </Alert>
          ) : null}
          {selectedBranch &&
            selectedEntertainment &&
            selectedProduct &&
            !entertainmentError &&
            (resources.length === 0 || selectedResource) && (
              <>
                <DateSelect onDateSelect={handleDateSelect} onError={handleDateError} />
                {selectedDate && !isDayBookable && (
                  <Alert severity='warning' sx={{ mt: 2 }}>
                    Keine Kapazitäten an diesem Tag verfügbar.
                  </Alert>
                )}
                {selectedDate && isDayBookable && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, md: 4 },
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                      }}
                    >
                      <Box sx={{ flex: 1, width: '100%', minHeight: { xs: 'auto', md: 'auto' } }}>
                        <PersonSelect
                          selectedDate={selectedDate}
                          selectedTime={selectedTime}
                          selectedDuration={selectedDuration}
                          onPersonSelect={handlePersonSelect}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          width: '100%',
                          minHeight: { xs: 'auto', md: 'auto' },
                          mt: { md: -2 },
                        }}
                      >
                        <DurationSelect
                          selectedDate={selectedDate}
                          onDurationSelect={handleDurationSelect}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <TimeSelect selectedDate={selectedDate} onTimeSelect={handleTimeSelect} />
                    </Box>
                  </Box>
                )}
              </>
            )}
        </Box>
      </Box>
      <Box sx={{ display: currentStep === 'packages' ? 'block' : 'none' }}>
        <PackageAndUpsellSelect isLoading={isLoading} />
      </Box>
      <Box sx={{ display: currentStep === 'customer' ? 'block' : 'none' }}>
        <CustomerData
          ref={customerDataRef}
          onWalkInSubmissionSelected={id => {
            selectedWalkInSubmissionIdRef.current = id;
          }}
        />
      </Box>
      <Box sx={{ display: currentStep === 'overview' ? 'block' : 'none' }}>
        <Overview
          ref={overviewRef}
          tempReservationId={tempReservationId}
          reservationCartSummary={reservationCartSummary}
        />
      </Box>
      <Box sx={{ display: currentStep === 'mailDelivery' ? 'block' : 'none' }}>
        {renderMailDeliveryContent()}
      </Box>
    </Box>
  );

  const renderButtons = () => {
    switch (currentStep) {
      case 'booking':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={handleGoToPackages}
              disabled={isLoading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {packages.length === 0 && upsells.length === 0
                ? 'Kundendaten eingeben'
                : 'Pakete auswählen'}
            </Button>
          </Stack>
        );
      case 'packages':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='space-between'
            alignItems='center'
            sx={{ width: '100%' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant='subtitle1'>
                Gesamtsumme: {calculateTotalAmount().toFixed(2).replace('.', ',')}€
              </Typography>
            </Box>
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={async () => {
                if (tempReservationId) {
                  try {
                    await invalidateTemporaryReservation(tempReservationId);
                    setTempReservationId(null);
                    setReservationCartSummary(null);
                  } catch (error) {
                    toast.error('Fehler beim Stornieren der Reservierung');
                    console.error('Error invalidating temporary reservation:', error);
                  }
                }
                setCurrentStep('booking');
              }}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={handleGoToCustomer}
              disabled={isPackagesLoading || isUpsellsLoading}
              sx={{
                borderWidth: '1px',
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Kundendaten eingeben
            </Button>
          </Stack>
        );
      case 'customer':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={async () => {
                if (packages.length === 0 && upsells.length === 0) {
                  await invalidateReservationIfAny();
                  setCurrentStep('booking');
                  return;
                }
                setCurrentStep('packages');
              }}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={() => {
                // Commit the latest field values and validate them so the user
                // always gets a concrete message about what is missing/invalid.
                const result = customerDataRef.current?.validateAndCommit?.();
                if (result && !result.isValid) {
                  toast.error(`Bitte vervollständige die Kundendaten: ${result.messages.join(', ')}`);
                  return;
                }
                // Persist customer data to localStorage for reuse within 15 minutes
                persistLastCustomerData(result?.data);
                calculateLineItems();
                setCurrentStep('overview');
              }}
              disabled={isLoading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Weiter
            </Button>
          </Stack>
        );
      case 'overview':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={() => setCurrentStep('customer')}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color={isRequestOnly && !hasConfirmPendingRight ? undefined : 'success'}
              onClick={handleCompleteBooking}
              disabled={isLoading || !areSpecialLineItemsComplete()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: '1px',
              }}
            >
              <LockIconWrapper right='internalBooking:create'>
                {isLoading
                  ? 'Buchung wird abgeschlossen...'
                  : isRequestOnly && !hasConfirmPendingRight
                    ? 'Anfrage stellen'
                    : 'Buchung abschließen'}
              </LockIconWrapper>
            </Button>
          </Stack>
        );
      case 'mailDelivery': {
        const status = mailDeliveryStatus?.status || 'pending';
        const canRetry = status === 'failed' || status === 'bounced';
        const canRefresh = status === 'pending' && mailStatusPollCount >= 22;
        const isDone = ['delivered', 'skipped'].includes(status);
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              onClick={finishCreatedBookingFlow}
              disabled={isResendingMail}
            >
              {isDone ? 'Fertig' : 'Schritt überspringen'}
            </Button>
            {canRefresh && (
              <Button
                variant='outlined'
                onClick={refreshMailDeliveryStatus}
                disabled={isMailStatusLoading || isResendingMail}
                startIcon={isMailStatusLoading ? <CircularProgress size={18} /> : null}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Status erneut prüfen
              </Button>
            )}
            {canRetry && (
              <Button
                variant='contained'
                color='success'
                onClick={handleRetryMailDelivery}
                disabled={isResendingMail}
                startIcon={
                  isResendingMail ? <CircularProgress size={18} color='inherit' /> : <SendIcon />
                }
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Erneut versenden
              </Button>
            )}
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleCloseRequest} disableEscapeKeyDown={true}>
        <Box
          sx={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '85%' },
            maxWidth: '1400px',
            maxHeight: { xs: '95vh', sm: '90vh' },
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            position: 'relative',
          }}
        >
          {currentStep === 'customer' && <CopyCustomerDataSelect />}
          {renderContent()}
          <Stack direction='column' spacing={2} sx={{ mt: { xs: 3, md: 4 } }}>
            {renderButtons()}
          </Stack>
        </Box>
      </Modal>

      <Dialog
        open={showRequestOnlyConfirm}
        onClose={() => setShowRequestOnlyConfirm(false)}
        aria-labelledby='request-only-dialog'
      >
        <DialogTitle id='request-only-dialog'>Buchung nur auf Anfrage</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Diese Buchung wird <strong>unter Vorbehalt</strong> angelegt und ist nicht garantiert.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Du kannst die Buchungsanfrage speichern oder (falls berechtigt) direkt bestätigen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRequestOnlyConfirm(false)} color='error'>
            Abbrechen
          </Button>
          <Button
            onClick={async () => {
              setShowRequestOnlyConfirm(false);
              await performCompleteBooking(false);
            }}
            color='primary'
          >
            <LockIconWrapper right='internalBooking:create'>Anfrage stellen</LockIconWrapper>
          </Button>
          {hasConfirmPendingRight ? (
            <Button
              onClick={async () => {
                setShowRequestOnlyConfirm(false);
                await performCompleteBooking(true);
              }}
              color='success'
              variant='contained'
            >
              <LockIconWrapper right='internalBooking:special:confirmpending'>
                Direkt bestätigen
              </LockIconWrapper>
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmClose}
        onClose={handleCancelClose}
        aria-labelledby='confirm-close-dialog'
      >
        <DialogTitle id='confirm-close-dialog'>Buchung abbrechen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du die Buchung wirklich abbrechen? Alle eingegebenen Daten gehen verloren.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color='primary'>
            Weiter buchen
          </Button>
          <Button onClick={handleConfirmClose} color='error' variant='contained'>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
